import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Gamepad2, Clock, Target, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECT_COLORS = {
  Finance: 'bg-emerald-100 text-emerald-700',
  Marketing: 'bg-blue-100 text-blue-700',
  HR: 'bg-purple-100 text-purple-700',
  Strategy: 'bg-orange-100 text-orange-700',
  Operations: 'bg-slate-100 text-slate-700',
};

export default function Simulations() {
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/simulations`);
        setSims(res.data || []);
      } catch {
        setSims([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-mono text-slate-600 mb-4">
              <Gamepad2 className="w-3.5 h-3.5" /> Experiential learning labs
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-slate-900 mb-4" data-testid="simulations-page-title">
              Simulations & Decision Labs
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Interactive management simulations built on the same cases in our library. Make decisions round by round and see the consequences unfold.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900" /></div>
          ) : sims.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-xl" data-testid="simulations-empty">
              <Gamepad2 className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-700 font-medium">Simulations are coming soon.</p>
              <p className="text-slate-500 text-sm mt-2">Our team is building interactive decision labs based on the cases you love. Check back shortly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sims.map(s => {
                const href = user ? `/simulations/${s.id}/play` : `/login?redirect=${encodeURIComponent(`/simulations/${s.id}/play`)}`;
                return (
                  <Link key={s.id} to={href} className="group bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-900 hover:shadow-md transition-all" data-testid={`sim-card-${s.id}`}>
                    <div className="flex items-center gap-2 mb-3">
                      {s.subject && (
                        <span className={`px-2.5 py-0.5 text-xs font-mono rounded ${SUBJECT_COLORS[s.subject] || 'bg-slate-100 text-slate-700'}`}>{s.subject}</span>
                      )}
                      <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-mono rounded uppercase">{(s.template_type || 'decision_lab').replace('_', ' ')}</span>
                    </div>
                    <h3 className="text-xl font-serif font-semibold text-slate-900 group-hover:text-orange-700 transition-colors mb-2">{s.title}</h3>
                    {s.description && <p className="text-sm text-slate-600 line-clamp-3 mb-4">{s.description}</p>}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mb-4">
                      {s.duration_minutes && (<span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration_minutes} min</span>)}
                      {s.difficulty && <span className="capitalize">• {s.difficulty}</span>}
                      {s.learner_role && (<span className="inline-flex items-center gap-1"><Target className="w-3.5 h-3.5" /> {s.learner_role}</span>)}
                    </div>
                    <div className="inline-flex items-center gap-1 text-orange-600 font-medium text-sm group-hover:gap-2 transition-all">
                      {user ? 'Launch simulation' : 'Sign in to launch'} <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
